import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Fade, Modal, Backdrop } from '@mui/material';
import { getMilitaryRankInfo } from '../utils/militaryRankUtils';
import './LevelUpAnimation.css';

const LevelUpAnimation = ({ level, onClose, open = true }) => {
  const { t } = useTranslation();
  const [animationPhase, setAnimationPhase] = useState('enter');
  const [showContent, setShowContent] = useState(false);

  // 使用统一的军衔工具函数
  const levelInfo = getMilitaryRankInfo(level);

  useEffect(() => {
    if (!open) return;

    // 动画序列
    const animationSequence = async () => {
      // 第一阶段：入场动画
      setAnimationPhase('enter');
      setShowContent(false);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // 第二阶段：显示内容
      setShowContent(true);
      setAnimationPhase('show');
      
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // 第三阶段：闪光效果
      setAnimationPhase('flash');
      
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // 第四阶段：退场动画
      setAnimationPhase('exit');
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // 关闭动画
      onClose?.();
    };

    animationSequence();
  }, [open, onClose]);

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={() => {}} // 禁用手动关闭
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          backdropFilter: 'blur(5px)'
        }
      }}
      container={() => document.body} // 确保直接挂载到body
      disablePortal={false} // 确保使用Portal
      sx={{
        // 强制全屏显示
        position: 'fixed !important',
        top: '0 !important',
        left: '0 !important',
        width: '100vw !important',
        height: '100vh !important',
        zIndex: 9999,
        // 移除任何可能的约束
        margin: '0 !important',
        padding: '0 !important',
        maxWidth: 'none !important',
        maxHeight: 'none !important',
        // 确保不受父容器影响
        transform: 'none !important'
      }}
    >
      <Fade in={open}>
        <Box className={`levelup-container levelup-${animationPhase}`}>
          {/* 背景粒子效果 */}
          <div className="particles-background">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${2 + Math.random() * 2}s`
                }}
              />
            ))}
          </div>

          {/* 主要内容 */}
          <Box className="levelup-content">
            {/* 等级图标 */}
            <Box className="level-icon-container">
              <Typography className="level-emoji" variant="h1">
                {levelInfo.stageIcon}
              </Typography>
              <Box className="level-glow" style={{ borderColor: levelInfo.color }} />
            </Box>

            {/* 等级信息 */}
            <Fade in={showContent} timeout={800}>
              <Box className="level-info">
                <Typography 
                  variant="h2" 
                  className="levelup-title"
                  style={{ color: levelInfo.color }}
                >
                  {t('level_up_title')}
                </Typography>
                
                <Typography 
                  variant="h3" 
                  className="level-name"
                  style={{ color: levelInfo.color }}
                >
                  {levelInfo.name}
                </Typography>
                
                <Typography variant="h4" className="level-number">
                  {t('level_up_level', { n: level })}
                </Typography>
                
                <Typography variant="body1" className="level-description">
                  {t('level_up_congrats')}
                </Typography>
              </Box>
            </Fade>

            {/* 装饰性光效 */}
            <div className="light-rays">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="light-ray"
                  style={{
                    transform: `rotate(${i * 45}deg)`,
                    animationDelay: `${i * 0.1}s`
                  }}
                />
              ))}
            </div>

            {/* 闪光效果 */}
            {animationPhase === 'flash' && (
              <div className="flash-overlay" />
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default LevelUpAnimation;